import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Power } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BaselineRow } from "@/components/consolidation/BaselineRow";
import { BaselineDiagnostics } from "@/components/consolidation/BaselineDiagnostics";
import { CertifiedBaselinePanel } from "@/components/consolidation/CertifiedBaselinePanel";
import { useAuth } from "@/contexts/AuthContext";

function ReasonDialog({ action, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => setReason(""), [action]);
  return (
    <Dialog open={!!action} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="consolidation-reason-dialog">
        <DialogHeader><DialogTitle>{action?.title}</DialogTitle><DialogDescription className="text-xs">{action?.description}</DialogDescription></DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Motivazione (obbligatoria)" data-testid="consolidation-reason-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="consolidation-reason-cancel">Annulla</Button>
          <Button onClick={() => onConfirm(reason)} disabled={reason.trim().length < 3} data-testid="consolidation-reason-confirm">Conferma</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CounterConsolidationAdmin() {
  const { user } = useAuth();
  const isSuperAdmin = ["admin", "super_admin"].includes(user?.role);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [data, setData] = useState(null);
  const [action, setAction] = useState(null);

  useEffect(() => { api.get("/counters-consolidation/companies").then(({ data: d }) => setCompanies(d.items)); }, []);
  const load = useCallback(() => {
    if (!companyId) { setData(null); return; }
    api.get(`/counters-consolidation/companies/${companyId}/baselines`).then(({ data: d }) => setData(d)).catch((e) => toast.error(apiErr(e)));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => { try { await fn(); toast.success(ok); setAction(null); load(); } catch (e) { toast.error(apiErr(e)); } };
  const generate = () => run(() => api.post(`/counters-consolidation/companies/${companyId}/baselines/generate`), "Proposte di baseline aggiornate");
  const decide = (b, approve) => setAction({
    title: approve ? "Approva baseline" : "Rifiuta baseline",
    description: `${b.employee_name} · ${b.label}. I dati vengono riverificati prima dell'approvazione.`,
    confirm: (reason) => run(() => api.post(`/counters-consolidation/baselines/${b.id}/${approve ? "approve" : "reject"}`, { reason }), approve ? "Baseline approvata" : "Baseline rifiutata"),
  });
  const toggle = (enabled) => setAction({
    title: enabled ? "Attiva consolidamento" : "Disattiva consolidamento",
    description: enabled ? "Richiede baseline approvate e valide per tutti i dipendenti con mesi chiusi." : "Non consentito se esistono fotografie o rettifiche.",
    confirm: (reason) => run(() => api.put(`/counters-consolidation/companies/${companyId}`, { enabled, reason }), enabled ? "Consolidamento attivato" : "Consolidamento disattivato"),
  });

  return (
    <div className="space-y-5" data-testid="counter-consolidation-admin">
      <div>
        <h1 className="text-2xl font-semibold text-suite-neutral-900">Consolidamento Contatori</h1>
        <p className="text-sm text-suite-neutral-600">Baseline ricostruite e attivazione controllata per azienda (solo Super amministratore).</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="h-9 rounded-md border border-suite-neutral-300 px-2 text-sm min-w-64" data-testid="consolidation-company-select">
          <option value="">Seleziona azienda…</option>
          {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}{c.enabled ? " · attivo" : ""}</option>)}
        </select>
        {data && (
          <>
            <Button variant="outline" onClick={generate} data-testid="generate-baselines-btn"><RefreshCw size={14} className="mr-1" />Genera / aggiorna baseline</Button>
            {data.enabled
              ? <Button variant="outline" onClick={() => toggle(false)} data-testid="disable-consolidation-btn"><Power size={14} className="mr-1" />Disattiva</Button>
              : <Button onClick={() => toggle(true)} disabled={data.activation_blockers.length > 0} data-testid="enable-consolidation-btn"><Power size={14} className="mr-1" />Attiva</Button>}
          </>
        )}
      </div>
      {data && (
        <>
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="consolidation-status">
            <b>{data.label}</b> · Stato: {data.enabled ? "ATTIVO" : "DISATTIVATO"} · Uso operativo: {data.operational_use.snapshots} fotografie, {data.operational_use.adjustments} rettifiche
            {data.activation_blockers.length > 0 && <div className="mt-1" data-testid="activation-blockers">Attivazione bloccata: {data.activation_blockers.join("; ")}</div>}
          </div>
          <div className="overflow-x-auto rounded-lg border border-suite-neutral-200 bg-white">
            <table className="w-full text-xs" data-testid="baselines-table">
              <thead className="bg-suite-neutral-50 text-suite-neutral-600">
                <tr>{["Dipendente", "Mese di riferimento", "Ferie", "ROL", "Ex festività", "Anomalie", "Stato", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.items.filter((b) => b.origin !== "payroll_verified").length === 0
                  ? <tr><td colSpan={8} className="px-3 py-4 text-suite-neutral-500">Nessuna baseline ricostruita: usare “Genera / aggiorna baseline”.</td></tr>
                  : data.items.filter((b) => b.origin !== "payroll_verified").map((b) => <BaselineRow key={b.id} b={b} onDecide={decide} />)}
              </tbody>
            </table>
          </div>
          {isSuperAdmin && <CertifiedBaselinePanel companyId={companyId} setAction={setAction} onChanged={load} />}
        </>
      )}
      {isSuperAdmin && <BaselineDiagnostics />}
      <ReasonDialog action={action} onClose={() => setAction(null)} onConfirm={(r) => action.confirm(r)} />
    </div>
  );
}
